import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AccountNav from "../components/AccountNav";
import { useAuth } from "../context/AuthContext";
import { addProperty, updateProperty } from "../services/api";

const MAX_IMAGES = 5;
const MAX_IMAGE_SIZE_BYTES = 500 * 1024;
const MIN_SALE_PRICE = 50000;
const MIN_RENT_PRICE = 500;
const MAX_RENT_PRICE = 200000;

const INITIAL_FORM = {
  title: "",
  description: "",
  type: "house",
  listing_type: "sale",
  contact_name: "",
  contact_phone: "",
  price: "",
  min_price: "",
  max_price: "",
  city: "",
  district: "",
  location_label: "",
  latitude: "",
  longitude: "",
  bedrooms: "",
  bathrooms: "",
  area_sqft: "",
  area_value: "",
  area_unit: "sq ft",
  property_images: [],
  shop_units: "",
  broker_name: ""  // [CHANGE 1] added broker_name
};

function formatPrice(value, listingType) {
  const amount = Number(value || 0);
  if (listingType === "rent") {
    return `₹ ${amount.toLocaleString("en-IN")}/mo`;
  }
  if (amount >= 10000000) {
    return `₹ ${(amount / 10000000).toFixed(amount >= 50000000 ? 0 : 1)} Crore`;
  }
  if (amount >= 100000) {
    return `₹ ${(amount / 100000).toFixed(amount >= 1000000 ? 0 : 1)} Lakh`;
  }
  return `₹ ${amount.toLocaleString("en-IN")}`;
}

function AddPropertyContent({
  form,
  loading,
  error,
  imageWarning,
  onChange,
  onRemoveImage,
  onSubmit,
  onboardingSummary,
  role,
  starterMessage,
  imagePreviews,
  isEdit = false,
  showCloseAction = false,
  onClose,
}) {
  const canUploadMoreImages = imagePreviews.length < MAX_IMAGES;
  const uploadButtonLabel = imagePreviews.length === 0 ? "Upload image" : "Upload another";

  return (
    <div className={`form-card ${showCloseAction ? "form-card--embedded" : ""}`.trim()}>
      <div className="form-header">
        <div className="fy-modal-head">
          <h2>{isEdit ? "Update Property" : "Add Property"}</h2>
        </div>
        <p className="page-subtitle">
          {role === "admin"
            ? "Admin listings can be published immediately."
            : "Common and Agent submissions will remain pending until admin approval."}
        </p>
      </div>

      {starterMessage ? <div className="feedback-banner success-banner">{starterMessage}</div> : null}
      {onboardingSummary ? <div className="feedback-banner info-banner">Starter details: {onboardingSummary}</div> : null}
      {error ? <div className="feedback-banner error-banner">{error}</div> : null}
      {imageWarning ? <div className="feedback-banner error-banner">{imageWarning}</div> : null}

      <form className="form-layout" onSubmit={onSubmit}>
        <section className="form-section">
          <div className="form-section-head">
            <p className="form-section-kicker">Basic details</p>
            <p className="form-section-copy">Start with the listing title, category, and buyer-facing description.</p>
          </div>

          <div className="form-grid">
            <input
              className="form-input"
              name="title"
              value={form.title}
              onChange={onChange}
              placeholder="Property title"
            />

            <select className="form-input" name="type" value={form.type} onChange={onChange}>
              <option value="house">House</option>
              <option value="land">Land</option>
              <option value="shop">Shop</option>
            </select>
            <select className="form-input" name="listing_type" value={form.listing_type} onChange={onChange}>
              <option value="sale">Sale</option>
              <option value="rent">Rent</option>
            </select>
          </div>

          <textarea
            className="form-input form-input--tall"
            name="description"
            value={form.description}
            onChange={onChange}
            placeholder="Description"
          />

          <div className="form-grid">
            {canUploadMoreImages ? (
              <label className="upload-image-trigger">
                {uploadButtonLabel}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  name="property_images"
                  onChange={onChange}
                  style={{ display: "none" }}
                />
              </label>
            ) : (
              <div className="feedback-banner info-banner" style={{ marginBottom: 0 }}>
                You can upload maximum {MAX_IMAGES} images.
              </div>
            )}
            <p className="form-section-copy" style={{ margin: 0 }}>
              Uploaded: {imagePreviews.length}/{MAX_IMAGES}
            </p>
          </div>
          {imagePreviews.length ? (
            <div className="form-grid">
              {imagePreviews.map((src, index) => (
                <div key={`${src.slice(0, 20)}-${index}`} className="upload-preview-card">
                  <img
                    src={src}
                    alt={`preview-${index + 1}`}
                    style={{ width: "100%", maxWidth: "180px", borderRadius: "10px", objectFit: "cover" }}
                  />
                  <button
                    type="button"
                    className="upload-preview-remove"
                    aria-label={`Remove image ${index + 1}`}
                    onClick={() => onRemoveImage(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </section>

        <section className="form-section">
          <div className="form-section-head">
            <p className="form-section-kicker">Contact details</p>
            <p className="form-section-copy">These will be saved from your account so enquiries reach the right person.</p>
          </div>

          <div className="form-grid">
            <input
              className="form-input"
              name="contact_name"
              value={form.contact_name}
              onChange={onChange}
              placeholder="Contact name"
            />
            <input
              className="form-input"
              name="contact_phone"
              value={form.contact_phone}
              onChange={onChange}
              placeholder="Contact phone"
            />
            {/* [CHANGE 2] broker_name input added after contact_phone */}
            <input
              className="form-input"
              name="broker_name"
              value={form.broker_name}
              onChange={onChange}
              placeholder="Broker / Agent name (visible to all)"
            />
          </div>
        </section>

        <section className="form-section">
          <div className="form-section-head">
            <p className="form-section-kicker">Location and pricing</p>
            <p className="form-section-copy">Add the key location details and commercial terms for this listing.</p>
          </div>

          <div className="form-grid">
            <input
  className="form-input"
  type="number"
  name="price"
  value={form.price}
  onChange={onChange}
  placeholder={form.listing_type === "rent" ? "Monthly rent (₹500 – ₹2,00,000)" : "Sale price (min ₹50,000)"}
  min={form.listing_type === "rent" ? MIN_RENT_PRICE : MIN_SALE_PRICE}
  max={form.listing_type === "rent" ? MAX_RENT_PRICE : undefined}
/>
            {form.price ? (
  <p className="form-section-copy" style={{ margin: 0, color: "var(--color-muted, #888)", textAlign: "right" }}>
    {formatPrice(form.price, form.listing_type)}
  </p>
) : null}
<p className="form-section-copy" style={{ margin: 0, color: "var(--color-muted, #888)" }}>
  {form.listing_type === "rent"
    ? "Main rent price shown on listing. Required."
    : "Main sale price shown on listing. Required."}
</p>
<input
  className="form-input"
  type="number"
  name="min_price"
  value={form.min_price}
  onChange={onChange}
  placeholder="Min price (optional)"
  min={0}
/>
            {form.min_price ? (
  <p className="form-section-copy" style={{ margin: 0, color: "var(--color-muted, #888)", textAlign: "right" }}>
    {formatPrice(form.min_price, form.listing_type)}
  </p>
) : null}
<input
  className="form-input"
  type="number"
  name="max_price"
  value={form.max_price}
  onChange={onChange}
  placeholder="Max price (optional)"
  min={0}
/>
            {form.max_price ? (
  <p className="form-section-copy" style={{ margin: 0, color: "var(--color-muted, #888)", textAlign: "right" }}>
    {formatPrice(form.max_price, form.listing_type)}
  </p>
) : null}
<p className="form-section-copy" style={{ margin: 0, color: "var(--color-muted, #888)" }}>
  Optional: Set a min and max to show a price range instead of a fixed price.
</p>
            {form.listing_type === "rent" ? (
              <p className="form-section-copy" style={{ margin: 0, color: "var(--color-muted, #888)" }}>
                Rent must be between ₹500 and ₹2,00,000 per month.
              </p>
            ) : (
              <p className="form-section-copy" style={{ margin: 0, color: "var(--color-muted, #888)" }}>
                Minimum sale price: ₹50,000.
              </p>
            )}
            <input
              className="form-input"
              name="city"
              value={form.city}
              onChange={onChange}
              placeholder="City"
            />
            <input
              className="form-input"
              name="district"
              value={form.district}
              onChange={onChange}
              placeholder="District"
            />
            <input
              className="form-input"
              name="location_label"
              value={form.location_label}
              onChange={onChange}
              placeholder="Address / locality"
            />
            <input
              className="form-input"
              type="number"
              name="latitude"
              value={form.latitude}
              onChange={onChange}
              placeholder="Latitude (optional)"
            />
            <input
              className="form-input"
              type="number"
              name="longitude"
              value={form.longitude}
              onChange={onChange}
              placeholder="Longitude (optional)"
            />
          </div>
        </section>

        <section className="form-section">
          <div className="form-section-head">
            <p className="form-section-kicker">Property specifics</p>
            <p className="form-section-copy">Add area for all property types using kanal, marla, or sq ft.</p>
          </div>

          <div className="form-grid">
            <input
              className="form-input"
              type="number"
              name="area_value"
              value={form.area_value}
              onChange={onChange}
              placeholder="Land area value"
            />
            <select className="form-input" name="area_unit" value={form.area_unit} onChange={onChange}>
              <option value="sq ft">Sq Ft</option>
              <option value="marla">Marla</option>
              <option value="kanal">Kanal</option>
            </select>
          </div>

          {form.type === "house" ? (
            <div className="form-grid">
              <input
                className="form-input"
                type="number"
                name="bedrooms"
                value={form.bedrooms}
                onChange={onChange}
                placeholder="Bedrooms"
              />
              <input
                className="form-input"
                type="number"
                name="bathrooms"
                value={form.bathrooms}
                onChange={onChange}
                placeholder="Bathrooms"
              />
            </div>
          ) : null}

          {form.type === "shop" ? (
            <div className="form-grid">
              <input
                className="form-input"
                type="number"
                name="shop_units"
                value={form.shop_units}
                onChange={onChange}
                placeholder="Shop units"
              />
              <input
                className="form-input"
                type="number"
                name="bathrooms"
                value={form.bathrooms}
                onChange={onChange}
                placeholder="Bathrooms (optional)"
              />
            </div>
          ) : null}
        </section>

        <div className="form-actions">
          <button className="btn btn-strong btn-full" type="submit" disabled={loading}>
            {loading ? (isEdit ? "Updating..." : "Submitting...") : (isEdit ? "Update Property" : "Submit Property")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AddProperty({
  embedded = false,
  initialPrefill = null,
  initialMessage = "",
  editPropertyId = null,
  onClose = null,
  onSuccess = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { role, user } = useAuth();

  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [imageWarning, setImageWarning] = useState("");
  const [imagePreviews, setImagePreviews] = useState([]);
  const starterMessage = initialMessage || location.state?.message || "";
  const prefill = initialPrefill || location.state?.prefill || null;

  useEffect(() => {
    const prefillImages = Array.isArray(prefill?.property_images) ? prefill.property_images : [];
    setForm((prev) => ({
      ...prev,
      title: prefill?.title || prev.title,
      description:
        prefill?.description ||
        prev.description ||
        (user?.mobile ? `Listing contact number: ${user.mobile}` : prev.description),
      type: prefill?.type || prev.type,
      listing_type: prefill?.listing_type || prev.listing_type,
      contact_name: prefill?.contact_name || prev.contact_name || user?.full_name || "",
      contact_phone: prefill?.contact_phone || prev.contact_phone || user?.mobile || "",
      price: prefill?.price != null ? String(prefill.price) : prev.price,
      min_price: prefill?.min_price != null ? String(prefill.min_price) : prev.min_price,
      max_price: prefill?.max_price != null ? String(prefill.max_price) : prev.max_price,
      city: prefill?.city || prev.city || "Srinagar",
      district: prefill?.district || prev.district || prev.city || "Srinagar",
      location_label: prefill?.location_label || prev.location_label || "",
      latitude: prefill?.latitude != null ? String(prefill.latitude) : prev.latitude,
      longitude: prefill?.longitude != null ? String(prefill.longitude) : prev.longitude,
      bedrooms: prefill?.bedrooms != null ? String(prefill.bedrooms) : prev.bedrooms,
      bathrooms: prefill?.bathrooms != null ? String(prefill.bathrooms) : prev.bathrooms,
      area_value: prefill?.area_value != null ? String(prefill.area_value) : prev.area_value,
      area_unit: prefill?.area_unit || prev.area_unit || "sq ft",
      property_images: prefillImages.length ? prefillImages : prev.property_images,
      shop_units: prefill?.shop_units != null ? String(prefill.shop_units) : prev.shop_units,
      broker_name: prefill?.broker_name || prev.broker_name || "",  // [CHANGE 4] broker_name in prefill block
    }));
    if (prefillImages.length) {
      setImagePreviews(prefillImages);
    }
  }, [
    prefill?.title,
    prefill?.description,
    prefill?.type,
    prefill?.listing_type,
    prefill?.contact_name,
    prefill?.contact_phone,
    prefill?.price,
    prefill?.min_price,
    prefill?.max_price,
    prefill?.city,
    prefill?.district,
    prefill?.location_label,
    prefill?.latitude,
    prefill?.longitude,
    prefill?.bedrooms,
    prefill?.bathrooms,
    prefill?.area_value,
    prefill?.area_unit,
    prefill?.shop_units,
    prefill?.broker_name,  // [CHANGE 4] broker_name in useEffect dependency array
    prefill?.property_images,
    user?.full_name,
    user?.mobile
  ]);

  const onboardingSummary = useMemo(() => {
    if (!user?.mobile) {
      return "";
    }
    return `Contact number on account: ${user.mobile}`;
  }, [user?.mobile]);

  const onChange = (event) => {
    const { name, value, files } = event.target;

    if (name !== "property_images") {
      setForm((prev) => ({ ...prev, [name]: value }));
      return;
    }

    setImageWarning("");
    const selectedFiles = Array.from(files || []);
    if (!selectedFiles.length) {
      event.target.value = "";
      return;
    }

    const warnings = [];
    const remainingSlots = Math.max(0, MAX_IMAGES - form.property_images.length);
    const limitedFiles = selectedFiles.slice(0, remainingSlots);
    if (selectedFiles.length > remainingSlots) {
      warnings.push(`You can upload maximum ${MAX_IMAGES} images. Extra files were ignored.`);
    }

    const validFiles = limitedFiles.filter((file) => {
      const isValid = file.size <= MAX_IMAGE_SIZE_BYTES;
      if (!isValid) {
        warnings.push(`${file.name} is larger than 500KB and was ignored.`);
      }
      return isValid;
    });

    if (!validFiles.length) {
      setImageWarning(warnings.join(" "));
      event.target.value = "";
      return;
    }

    Promise.all(
      validFiles.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result || "");
            reader.onerror = () => resolve("");
            reader.readAsDataURL(file);
          })
      )
    ).then((encodedImages) => {
      const cleanImages = encodedImages.filter(Boolean);
      setForm((prev) => ({ ...prev, property_images: [...prev.property_images, ...cleanImages] }));
      setImagePreviews((prev) => [...prev, ...cleanImages]);
      setImageWarning(warnings.join(" "));
      event.target.value = "";
    });
  };

  const onRemoveImage = (indexToRemove) => {
    setForm((prev) => ({
      ...prev,
      property_images: prev.property_images.filter((_, index) => index !== indexToRemove),
    }));
    setImagePreviews((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const toNumberOrNull = (value) => {
    if (value === "" || value === null || value === undefined) {
      return null;
    }
    const numeric = Number(value);
    return Number.isNaN(numeric) ? null : numeric;
  };

  const buildPayload = () => {
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      type: form.type,
      listing_type: form.listing_type,
      contact_name: form.contact_name.trim() || user?.full_name || null,
      contact_phone: form.contact_phone.trim() || user?.mobile || null,
      broker_name: form.broker_name.trim() || null,  // [CHANGE 3] broker_name in buildPayload
      price: toNumberOrNull(form.price),
      min_price: toNumberOrNull(form.min_price),
      max_price: toNumberOrNull(form.max_price),
      city: form.city.trim() || null,
      district: form.district.trim() || form.city.trim() || null,
      location_label: form.location_label.trim() || null,
      latitude: toNumberOrNull(form.latitude),
      longitude: toNumberOrNull(form.longitude),
      bedrooms: null,
      bathrooms: null,
      area_sqft: null,
      area_value: toNumberOrNull(form.area_value),
      area_unit: form.area_unit || null,
      property_images: Array.isArray(form.property_images) ? form.property_images : [],
      shop_units: null
    };

    if (form.type === "house") {
      payload.bedrooms = toNumberOrNull(form.bedrooms);
      payload.bathrooms = toNumberOrNull(form.bathrooms);
    }

    if (form.type === "shop") {
      payload.shop_units = toNumberOrNull(form.shop_units);
      payload.bathrooms = toNumberOrNull(form.bathrooms);
    }

    return payload;
  };

  const validate = () => {
    if (!form.title.trim()) {
      return "Title is required.";
    }

    const priceNum = Number(form.price);
      const hasRange = form.min_price && form.max_price;

      if (!hasRange) {
  if (!form.price || priceNum <= 0) {
    return "Price is required when min and max price are not both set.";
  }

  if (form.listing_type === "sale") {
    if (priceNum < MIN_SALE_PRICE) {
      return `Sale listings must have a minimum price of ₹${MIN_SALE_PRICE.toLocaleString("en-IN")}.`;
    }
  } else if (form.listing_type === "rent") {
    if (priceNum < MIN_RENT_PRICE) {
      return `Rent must be at least ₹${MIN_RENT_PRICE.toLocaleString("en-IN")} per month.`;
    }
    if (priceNum > MAX_RENT_PRICE) {
      return `Rent cannot exceed ₹${MAX_RENT_PRICE.toLocaleString("en-IN")} per month.`;
    }
  }
}

const minPriceNum = toNumberOrNull(form.min_price);
const maxPriceNum = toNumberOrNull(form.max_price);

if (minPriceNum !== null && maxPriceNum !== null && minPriceNum > maxPriceNum) {
  return "Min price cannot be greater than max price.";
}

    if (!form.contact_name.trim()) {
      return "Contact name is required.";
    }

    if (!form.contact_phone.trim()) {
      return "Contact phone is required.";
    }

    if (form.property_images.length > MAX_IMAGES) {
      return `Maximum ${MAX_IMAGES} images are allowed.`;
    }

    if (form.type === "house") {
      if (!form.bedrooms || Number(form.bedrooms) < 0) {
        return "House must include bedrooms.";
      }
      if (!form.bathrooms || Number(form.bathrooms) < 0) {
        return "House must include bathrooms.";
      }
    }

    if (!form.area_value || Number(form.area_value) <= 0) {
      return "Area value must be greater than 0.";
    }

    if (!form.area_unit) {
      return "Area unit is required.";
    }

    if (form.type === "shop") {
      if (!form.shop_units || Number(form.shop_units) <= 0) {
        return "Shop must include number of units.";
      }
    }

    return "";
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      const payload = buildPayload();
      const created = editPropertyId
        ? await updateProperty(editPropertyId, payload)
        : await addProperty(payload);
      const isPublished = created.status === "published";
      const message = editPropertyId
        ? "Property updated successfully."
        : isPublished
          ? "Property published successfully."
          : "Property submitted and moved to pending approval.";

      if (onSuccess) {
        onSuccess(created, message);
        return;
      }

      navigate("/listings", {
        state: {
          message
        }
      });
    } catch (requestError) {
      const message = requestError?.response?.data?.detail || "Failed to submit property.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <AddPropertyContent
      form={form}
      loading={loading}
      error={error}
      imageWarning={imageWarning}
      onChange={onChange}
      onRemoveImage={onRemoveImage}
      onSubmit={onSubmit}
      onboardingSummary={onboardingSummary}
      role={role}
      starterMessage={starterMessage}
      imagePreviews={imagePreviews}
      isEdit={Boolean(editPropertyId)}
      showCloseAction={embedded}
      onClose={onClose}
    />
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="container page account-page add-property-page">
      <div className="account-shell">
        <AccountNav />

        <section className="account-main">
          {content}
        </section>
      </div>
    </div>
  );
}